import React, { useEffect, useRef } from 'react';
import AppButton from '../Button/AppButton';

const ConfirmModal = ({
    id,
    // Trigger opcional (uso 1): si se pasan, se renderiza el botón que abre el modal por sí mismo.
    triggerLabel,
    triggerClassName = 'btn btn-default btn-primary__submit btn-color-rojo-borde',
    // Control externo opcional (uso 2): si se pasa `show`, el modal se abre/cierra según ese valor.
    show,
    onHidden,
    // Contenido
    title,
    body,
    titleBold = true,
    // Botón izquierdo (opcional). Si no se pasa cancelLabel, no se renderiza ese botón
    // y el modal se comporta con un solo botón de acción (como en la mayoría de casos).
    cancelLabel,
    cancelClassName = 'btn btn-default btn-color-plomo-full mr-2',
    cancelStyle,
    // Si se pasa, el botón "cancelar" se renderiza con AppButton (jerarquía de variantes),
    // ignorando cancelClassName/cancelStyle. Si no se pasa, sigue usándose <button> nativo
    // con className/style, para no forzar el cambio en consumidores existentes (ej. "Inactivar").
    cancelVariant,
    onCancel,
    cancelDismiss,
    // Botón derecho / principal
    confirmLabel = 'Confirmar',
    confirmClassName = 'btn btn-default btn-color-rojo-borde',
    confirmStyle,
    confirmVariant,
    onConfirm,
    confirmDismiss,
}) => {
    const modalRef = useRef(null);
    const wasEverShownRef = useRef(false);
    const isControlled = typeof show === 'boolean';
    // En modo controlado, el cierre lo decide el padre (vía la prop `show`), no data-dismiss,
    // para evitar que dos mecanismos intenten cerrar el modal a la vez. En modo trigger propio
    // (como "Inactivar"), data-dismiss sigue siendo el comportamiento por defecto.
    const resolvedCancelDismiss = typeof cancelDismiss === 'boolean' ? cancelDismiss : !isControlled;
    const resolvedConfirmDismiss = typeof confirmDismiss === 'boolean' ? confirmDismiss : !isControlled;

    // El proyecto carga una segunda versión de jQuery más abajo en index.html (para librerías
    // legacy), que sobrescribe window.$ con una instancia sin el plugin de Bootstrap Modal.
    // window.$bootstrapJQuery guarda la referencia correcta (ver index.html), con fallback a
    // window.$ por si el entorno no la tuviera disponible.
    const getBootstrapJQuery = () => window.$bootstrapJQuery || window.$;

    // Modo controlado (uso 2): sincroniza el modal de Bootstrap con la prop `show`.
    // No se llama a .modal('hide') mientras nunca se haya mostrado el modal: este componente
    // puede montarse siempre en pantalla (ej. dentro de un Provider a nivel raíz) con `show`
    // en false desde el inicio, y no hay necesidad de tocar el plugin de Bootstrap hasta que
    // realmente se pida abrir el modal por primera vez.
    useEffect(() => {
        if (!isControlled) return;
        const $ = getBootstrapJQuery();
        const $modal = $ && modalRef.current ? $(modalRef.current) : null;
        if (!$modal) return;

        if (show) {
            wasEverShownRef.current = true;
            $modal.modal('show');
        } else if (wasEverShownRef.current) {
            $modal.modal('hide');
        }
    }, [isControlled, show]);

    // Si el usuario cierra el modal por un medio propio de Bootstrap (Escape, clic en backdrop,
    // data-dismiss), se avisa al padre para que sincronice su propio estado.
    useEffect(() => {
        const $ = getBootstrapJQuery();
        const $modal = $ && modalRef.current ? $(modalRef.current) : null;
        if (!$modal || !onHidden) return;
        const handleHidden = () => onHidden();
        $modal.on('hidden.bs.modal', handleHidden);
        return () => { $modal.off('hidden.bs.modal', handleHidden); };
    }, [onHidden]);

    return (
        <>
            {triggerLabel && (
                <button type="button" className={triggerClassName} data-toggle="modal" data-target={`#${id}`}>
                    {triggerLabel}
                </button>
            )}
            <div className="modal fade" id={id} ref={modalRef} tabIndex="-1" role="dialog" aria-labelledby="" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-body text-center py-4">
                            {title && (
                                <p className="mb-4" style={{fontWeight: titleBold ? 'bold' : 'normal'}}>
                                    {title}
                                </p>
                            )}
                            {body && (
                                <p style={{marginBottom: '20px'}}>{body}</p>
                            )}
                            <div className="d-flex justify-content-center">
                                {cancelLabel && (
                                    cancelVariant ? (
                                        <AppButton
                                            variant={cancelVariant}
                                            style={{marginRight: '10px'}}
                                            data-dismiss={resolvedCancelDismiss ? 'modal' : undefined}
                                            onClick={onCancel}
                                        >
                                            {cancelLabel}
                                        </AppButton>
                                    ) : (
                                        <button
                                            type="button"
                                            className={cancelClassName}
                                            style={{marginRight: '10px', ...cancelStyle}}
                                            data-dismiss={resolvedCancelDismiss ? 'modal' : undefined}
                                            onClick={onCancel}
                                        >
                                            {cancelLabel}
                                        </button>
                                    )
                                )}
                                {confirmVariant ? (
                                    <AppButton
                                        variant={confirmVariant}
                                        data-dismiss={resolvedConfirmDismiss ? 'modal' : undefined}
                                        onClick={onConfirm}
                                    >
                                        {confirmLabel}
                                    </AppButton>
                                ) : (
                                    <button
                                        type="button"
                                        className={confirmClassName}
                                        style={confirmStyle}
                                        data-dismiss={resolvedConfirmDismiss ? 'modal' : undefined}
                                        onClick={onConfirm}
                                    >
                                        {confirmLabel}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConfirmModal;
